/**
 * Applies a side-panel field edit (a `setFields` operation from the issue viewer or an item
 * picker) to one board card.
 *
 * The edit is in the issue viewer's shape (`titleHTML`, uppercase `state`, label `edges`, a status
 * with a nested `category`), and the card is in the board payload's shape (`titleHtml`, lowercase
 * `state`, `fields.label`, a flat status), so this translates between them. Custom issue fields
 * are edited by a different operation, in `custom-issue-fields.ts`.
 *
 * Returns the same card when nothing applies, so a card the edit doesn't change keeps its
 * identity. Only fields delivered with the board are updated here. Deferred fields refresh
 * separately after the edit is confirmed.
 */

import {sanitize} from '@github-ui/sanitize'

import type {
  BoardPayloadAssignee,
  BoardPayloadIssueStatus,
  BoardPayloadIssueType,
  BoardPayloadItem,
  BoardPayloadLabel,
  BoardPayloadMilestone,
  BoardPayloadParentIssue,
  BoardPayloadSubIssuesProgress,
  IssueState,
  IssueStateReason,
} from '../board-payload'

type EditFields = Readonly<Record<string, unknown>>

export function applyFieldsEditToCard(item: BoardPayloadItem, edit: EditFields): BoardPayloadItem {
  const top: Partial<Pick<BoardPayloadItem, 'title' | 'titleHtml'>> & {
    state?: IssueState
    stateReason?: IssueStateReason | null
    status?: BoardPayloadIssueStatus | null
  } = {}

  if (typeof edit.title === 'string') top.title = edit.title
  if (typeof edit.titleHTML === 'string') top.titleHtml = edit.titleHTML

  if (item.type === 'issue') {
    const state = toIssueState(edit.state)
    if (state !== undefined) top.state = state
    const stateReason = toIssueStateReason(edit.stateReason)
    if (stateReason !== undefined) top.stateReason = stateReason
    const status = toIssueStatus(edit.status)
    if (status !== undefined) top.status = status
  }

  const slots = toFieldSlots(item, edit)

  if (Object.keys(top).length === 0 && slots === undefined) return item

  return {
    ...item,
    ...top,
    ...(slots === undefined ? {} : {fields: {...item.fields, ...slots}}),
  }
}

function toFieldSlots(item: BoardPayloadItem, edit: EditFields): Record<string, {value: unknown}> | undefined {
  const fields: Record<string, unknown> = item.fields
  const slots: Record<string, {value: unknown}> = {}

  if ('label' in fields) {
    const labels = toLabels(edit.labels, item.fields.label?.value ?? [])
    if (labels !== undefined) slots.label = {value: labels}
  }

  if ('assignee' in fields) {
    const assignees = toAssignees(edit.assignedActors)
    if (assignees !== undefined) slots.assignee = {value: assignees}
  }

  if ('milestone' in fields) {
    const milestone = toMilestone(edit.milestone)
    if (milestone !== undefined) slots.milestone = {value: milestone}
  }

  if ('closed' in fields && (edit.closedAt === null || typeof edit.closedAt === 'string')) {
    slots.closed = {value: edit.closedAt}
  }

  if (item.type === 'issue' && 'issue_type' in fields) {
    const issueType = toIssueType(edit.issueType)
    if (issueType !== undefined) slots.issue_type = {value: issueType}
  }

  if (item.type === 'issue' && 'parent-issue' in fields) {
    const parent = toParentIssue(edit.parent, item.fields['parent-issue']?.value ?? null)
    if (parent !== undefined) slots['parent-issue'] = {value: parent}
  }

  if (item.type === 'issue' && 'sub-issues-progress' in fields) {
    const progress = toSubIssuesProgress(edit.subIssuesSummary)
    if (progress !== undefined) slots['sub-issues-progress'] = {value: progress}
  }

  return Object.keys(slots).length === 0 ? undefined : slots
}

function toIssueState(value: unknown): IssueState | undefined {
  if (value === 'OPEN' || value === 'open') return 'open'
  if (value === 'CLOSED' || value === 'closed') return 'closed'
  return undefined
}

const STATE_REASONS: ReadonlySet<string> = new Set<IssueStateReason>([
  'not_planned',
  'completed',
  'reopened',
  'duplicate',
])

function toIssueStateReason(value: unknown): IssueStateReason | null | undefined {
  if (value === null) return null
  if (typeof value !== 'string') return undefined
  const reason = value.toLowerCase()
  return STATE_REASONS.has(reason) ? (reason as IssueStateReason) : undefined
}

/**
 * The status picker sends `{id, name, category: {key, state, stateReason}}` in GraphQL casing. The
 * card stores the status's state and reason flat, in lowercase, with the category as its key.
 */
function toIssueStatus(value: unknown): BoardPayloadIssueStatus | null | undefined {
  if (value === null) return null
  if (!isRecord(value) || typeof value.name !== 'string' || !isRecord(value.category)) return undefined

  const {category} = value
  const state = toIssueState(category.state)
  const stateReason = toIssueStateReason(category.stateReason ?? null)
  if (state === undefined || stateReason === undefined) return undefined

  return {
    name: value.name,
    state,
    category: typeof category.key === 'string' ? category.key : undefined,
    stateReason,
  }
}

/**
 * The edit names labels by GraphQL node ID, and the card by database ID. The card keys labels
 * by name and never reads the ID, so a label the card already shows keeps its ID, and a newly
 * added one holds 0 until the next board read replaces it.
 */
function toLabels(value: unknown, current: readonly BoardPayloadLabel[]): BoardPayloadLabel[] | undefined {
  const nodes = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.edges)
      ? value.edges.map(edge => (isRecord(edge) ? edge.node : undefined))
      : undefined
  if (nodes === undefined) return undefined

  const idsByName = new Map(current.map(label => [label.name, label.id]))
  const labels: BoardPayloadLabel[] = []
  for (const node of nodes) {
    if (
      !isRecord(node) ||
      typeof node.name !== 'string' ||
      typeof node.nameHTML !== 'string' ||
      typeof node.color !== 'string'
    ) {
      return undefined
    }
    labels.push({
      id: idsByName.get(node.name) ?? 0,
      name: node.name,
      nameHtml: node.nameHTML,
      color: node.color,
      description: typeof node.description === 'string' ? node.description : null,
    })
  }
  return labels
}

function toAssignees(value: unknown): BoardPayloadAssignee[] | undefined {
  if (!isRecord(value) || !Array.isArray(value.nodes)) return undefined

  const assignees: BoardPayloadAssignee[] = []
  for (const node of value.nodes) {
    if (!isRecord(node) || typeof node.login !== 'string' || typeof node.avatarUrl !== 'string') return undefined
    assignees.push({
      login: node.login,
      name: typeof node.name === 'string' ? node.name : null,
      avatarUrl: node.avatarUrl,
      isAgent: node.isAgent === true,
      isCopilot: node.isCopilot === true,
    })
  }
  return assignees
}

function toMilestone(value: unknown): BoardPayloadMilestone | null | undefined {
  if (value === null) return null
  if (!isRecord(value) || typeof value.title !== 'string' || typeof value.url !== 'string') return undefined

  const state = typeof value.closed === 'boolean' ? (value.closed ? 'closed' : 'open') : toIssueState(value.state)
  if (state === undefined) return undefined

  return {
    title: value.title,
    url: value.url,
    state,
    dueOn: typeof value.dueOn === 'string' ? value.dueOn : null,
  }
}

function toIssueType(value: unknown): BoardPayloadIssueType | null | undefined {
  if (value === null) return null
  if (!isRecord(value) || typeof value.name !== 'string' || typeof value.color !== 'string') return undefined
  return {name: value.name, color: value.color}
}

/**
 * The sub-issues list and the issue viewer's parent picker send `{title, titleHTML, number, url,
 * state, stateReason}` in GraphQL casing. The confirmed edit adds the parent's status; neither has
 * when it was updated. A card that keeps the same parent keeps what the edit omits; a new parent
 * shows its state until the server confirms its status.
 */
function toParentIssue(
  value: unknown,
  current: BoardPayloadParentIssue | null,
): BoardPayloadParentIssue | null | undefined {
  if (value === null) return null
  if (
    !isRecord(value) ||
    typeof value.titleHTML !== 'string' ||
    typeof value.number !== 'number' ||
    typeof value.url !== 'string'
  ) {
    return undefined
  }
  const state = toIssueState(value.state)
  const stateReason = toIssueStateReason(value.stateReason ?? null)
  if (state === undefined || stateReason === undefined) return undefined

  const same = current?.url === value.url
  const status = 'status' in value ? toIssueStatus(value.status) : undefined
  return {
    titleHtml: sanitize(value.titleHTML, {preset: 'inline'}),
    number: value.number,
    state,
    stateReason,
    status: status !== undefined ? status : same ? current.status : undefined,
    url: value.url,
    // The edit omits updatedAt; preserve the hovercard cache version only for the same parent.
    updatedAt: same ? current.updatedAt : '',
  }
}

/** The card shows no progress for an issue without sub-issues, and rounds down as the sub-issues list does. */
function toSubIssuesProgress(value: unknown): BoardPayloadSubIssuesProgress | null | undefined {
  if (!isRecord(value) || typeof value.total !== 'number' || typeof value.completed !== 'number') return undefined
  const {total, completed} = value
  if (total === 0) return null
  return {total, completed, percentCompleted: Math.floor((completed / total) * 100)}
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}
